import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./DetailsMonthlyPerformance.css";
import axiosInstance from "../../../Config/axiosInstance";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DataGrid } from "@mui/x-data-grid";
import IconButton from '@mui/material/IconButton';
import EditNoteSharpIcon from '@mui/icons-material/Edit';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  DialogContentText,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

const DetailsMonthlyPerformance = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [canEdit, setCanEdit] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [rows, setRows] = useState([]);
  const [formData, setFormData] = useState({
    cost_id: "",
    financial_period: "",
    percentage: "",
    completion_percentage: "",
    created_at: "",
  });
  const [canDelete, setCanDelete] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  useEffect(() => {
    const requiredPath = "/monthly-percent";
    const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
    const permissions = storedPermissions?.pages || [];

    setCanEdit(permissions.some((page) => new URL(page.url).pathname === requiredPath && page.can_edit));
    setCanDelete(permissions.some((page) => new URL(page.url).pathname === requiredPath && page.can_delete));
  }, []);

  useEffect(() => {
    axiosInstance.get(`/monthly-performance/${id}/`)
      .then((response) => {
        const data = response.data;
        setFormData({
          cost_id: data.cost_id,
          financial_period: data.financial_period,
          percentage: data.percentage,
          completion_percentage: data.completion_percentage,
          created_at: data.created_at,
        });
      })
      .catch((error) =>
        console.error("Eroare la preluarea datelor:", error)
      );
  }, [id]);

  const handleEditClick = (row) => {
    setSelectedId(row.id);
    setFormData({ ...row });
    setPopupOpen(true);
  };


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleClosePopup = () => {
    setPopupOpen(false);
    setSelectedId(null);
  };

  //Stergere
  const handleDeleteClick = (row) => {
    setItemToDelete(row);
    setOpenDeleteDialog(true);
  };

  const handleSave = async () => {
    try {
      const { percentage } = formData;
      const response = await axiosInstance.put(`/monthly-performance/${selectedId}/`, {
        percentage,
      });

      const updatedRows = rows.map(row =>
        row.id === selectedId
          ? { ...row, percentage: response.data.percentage, completion_percentage: response.data.completion_percentage }
          : row
      );
      setRows(updatedRows);

      setPopupOpen(false);
      setSelectedId(null);
    } catch (error) {
      console.error("Eroare la salvare:", error);
    }
  };


  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          monthlyPerformance,
          projectBudget,
          financialPeriod,
          budgetStructure
        ] = await Promise.all([
          axiosInstance.get('/monthly-performance/'),
          axiosInstance.get('/project-budget/'),
          axiosInstance.get('/financial-period/'),
          axiosInstance.get('/budget-structure/')
        ]);

        const PBtCostDescriptionMap = projectBudget.data.reduce((acc, project_budget) => {
          acc[project_budget.id] = project_budget.cost_element;
          return acc;
        }, {});

        const PBtCostIdMap = projectBudget.data.reduce((acc, project_budget) => {
          acc[project_budget.id] = project_budget.cost_element;
          return acc;
        }, {});

        const PBProjectCodeMap = projectBudget.data.reduce((acc, project_budget) => {
          acc[project_budget.id] = project_budget.cost_element;
          return acc;
        }, {});

        const financialPeriodMap = financialPeriod.data.reduce((acc, item) => {
          acc[item.id] = `${item.month} ${item.year}`;
          return acc;
        }, {});

        const CostDescriptionMap = budgetStructure.data.reduce((acc, item) => {
          acc[item.id] = item.cost_element_description;
          return acc;
        }, {});

        const CostIdMap = budgetStructure.data.reduce((acc, item) => {
          acc[item.id] = item.cost_element_id;
          return acc;
        }, {});

        const ProjectCodeMap = budgetStructure.data.reduce((acc, item) => {
          acc[item.id] = item.project_code_details?.project_code ?? '-';
          return acc;
        }, {});

        // obținem toate performanțele pentru livrabilul selectat
        const allPerformance = monthlyPerformance.data.filter(
          item => item.cost_id === formData.cost_id
        );

        //  obținem cost_element_description chiar dacă toate valorile sunt 0%
        if (allPerformance.length > 0) {
          setFormData(prev => ({
            ...prev,
            cost_element_description:
              CostDescriptionMap[PBtCostDescriptionMap[formData.cost_id]] || 'N/A'
          }));
        }

        //  afișăm doar rândurile cu procentaj > 0 în tabel
        const filteredPerformance = allPerformance.filter(item => item.percentage > 0);

        const sortedData = filteredPerformance.map((item) => ({
          id: item.id,
          project_code: ProjectCodeMap[PBProjectCodeMap[item.cost_id]] || 'N/A',
          cost_element_id: CostIdMap[PBtCostIdMap[item.cost_id]] || 'N/A',
          cost_element_description: CostDescriptionMap[PBtCostDescriptionMap[item.cost_id]] || 'N/A',
          financial_period: financialPeriodMap[item.financial_period] || 'N/A',
          percentage: item.percentage,
          completion_percentage: item.completion_percentage,
          created_at: item.created_at,
          cost_id: item.cost_id
        })).sort((a, b) => b.id - a.id);

        setRows(sortedData);
      } catch (error) {
        console.error('Eroare la obținerea datelor:', error.response?.data || error.message);
      }
    };

    if (formData.cost_id) {
      fetchData();
    }
  }, [formData.cost_id]);


  const columns = [
    { field: "financial_period", headerName: "Month", flex: 1, minWidth: 115 },
    { field: "percentage", headerName: "Monthly Percentage", flex: 1, minWidth: 110, renderCell: (params) => <span>{params.value}%</span> },
  ];

  //Adaugarea coloanei de editare/stergere doar daca utilizatorul are permisiune
  if (canEdit || canDelete) {
    columns.push({
      field: 'action',
      headerName: 'Action',
      width: 100,
      renderCell: (params) => (
        <>
          {canEdit && (
            <IconButton onClick={() => handleEditClick(params.row)} sx={{ color: '#1976d2' }}>
              <EditNoteSharpIcon />
            </IconButton>
          )}
          {canDelete && (
            <IconButton onClick={() => handleDeleteClick(params.row)} sx={{ color: '#d32f2f' }}>
              <DeleteIcon />
            </IconButton>
          )}
        </>
      ),
      sortable: false,
      disableColumnMenu: true,
    });
  }

  return (
    <div className="details-percentage-page">
      <div className="details-percentage-container">
        <div className="header">
          <ArrowBackIcon className="back-arrow" onClick={() => navigate(-1)} />
          <h2 className="add-supplier-title">Monthly Percent Details</h2>
        </div>
      </div>

      <hr className="title-separator" />

      <label htmlFor="cost_element_id" className="subtitlu">Cost Description</label>
      <div className="details-percentage-container">
        <div className="form-group">
          <input
            type="text"
            id="cost_element_description"
            name="cost_element_description"
            value={formData.cost_element_description || ""}
            onChange={handleChange}
            className="input"
            disabled
          />
        </div>
      </div>

      <label className="subtitlu">Monthly Percent</label>
      <div className="details-percentage-container">

        <DataGrid
          id="monthly-percentage-table"
          rows={rows}
          columns={columns}
          initialState={{
            sorting: {
              sortModel: [{ field: "id", sort: "desc" }],
            },
            pagination: {
              paginationModel: { pageSize: 12 },
            },
          }}
          disableRowSelectionOnClick={true}
          pageSizeOptions={[10, 12, 20, 25]}
        />
      </div>

      <Dialog
        open={popupOpen}
        onClose={handleClosePopup}
        maxWidth="sm"
        fullWidth
        className="add-popup-monthly-percentage-container"
      >
        <DialogTitle className="title-popup">Edit Percentage</DialogTitle>
        <DialogContent dividers>
          <div className="form-group">
            <label htmlFor="project_code" className="label">Project Code</label>
            <input
              type="text"
              id="project_code"
              name="project_code"
              value={formData.project_code || ''}
              className="input"
              disabled
            />
          </div>

          <div className="form-group">
            <label htmlFor="cost_element_id" className="label">Cost ID</label>
            <input
              type="text"
              id="cost_element_id"
              name="cost_element_id"
              value={formData.cost_element_id || ''}
              className="input"
              disabled
            />
          </div>

          <div className="form-group">
            <label htmlFor="created_at" className="label">Created At</label>
            <input
              type="text"
              id="created_at"
              name="created_at"
              value={formData.created_at ? new Date(formData.created_at).toLocaleString() : ''}
              className="input"
              disabled
            />
          </div>

          <div className="form-group">
            <label htmlFor="financial_period" className="label">Month</label>
            <input
              type="text"
              id="financial_period"
              name="financial_period"
              value={formData.financial_period || ''}
              className="input"
              disabled
            />
          </div>

          <div className="form-group">
            <label htmlFor="percentage" className="label">Monthly Percentage <span style={{ color: 'red' }}>*</span></label>
            <input
              type="text"
              id="percentage"
              name="percentage"
              value={formData.percentage || ''}
              onChange={handleChange}
              className="input"
            />
          </div>

        </DialogContent>

        <DialogActions>
          <Button onClick={handleClosePopup} className="button discard">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} className="button save">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog stergere */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this record?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            sx={{
              backgroundColor: '#e0e0e0',
              color: '#424242',
              '&:hover': {
                backgroundColor: '#bdbdbd',
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              try {
                await axiosInstance.delete(`/monthly-performance/${itemToDelete.id}/`);
                setRows(prevRows => prevRows.filter(r => r.id !== itemToDelete.id));
                setOpenDeleteDialog(false);
              } catch (error) {
                console.error('Error deleting record:', error.response?.data || error.message);
              }
            }}
            sx={{
              backgroundColor: '#d32f2f',
              color: 'white',
              '&:hover': {
                backgroundColor: '#b71c1c',
              }
            }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DetailsMonthlyPerformance;
